import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Timer } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/app/AppShell";
import { FocusTab } from "@/pages/Workspace";
import { getSoloFocus } from "@/lib/api";

export default function SoloFocus() {
  const [params] = useSearchParams();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const result = await getSoloFocus();
    setOverview(result);
  };

  useEffect(() => {
    let active = true;
    getSoloFocus()
      .then((result) => { if (active) setOverview(result); })
      .catch((error) => toast("Could not load solo focus.", { description: error.message }))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-6 py-10 sm:px-10">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="overline mb-3">Tools · solo</div>
            <h1 className="font-serif-editorial text-4xl font-light tracking-tighter sm:text-5xl">Focus room</h1>
            <p className="mt-3 max-w-2xl text-sm font-light text-[#A1A1AA]">Choose one task, set a clear first action, and protect a block of time. You can use this room before or after finding a partner.</p>
          </div>
          <Link to="/tasks" className="hm-btn-ghost inline-flex items-center gap-2 rounded-md px-4 py-2 text-xs"><ArrowRight className="h-4 w-4" /> Your tasks</Link>
        </div>

        {loading ? <div className="text-sm text-[#A1A1AA]">Loading focus room...</div> : overview ? (
          <>
            <FocusTab p={{ focus: overview.stats }} partner={null} refresh={refresh} solo initialTaskId={params.get("task") || ""} />
            <p className="mt-5 text-xs text-[#A1A1AA]">Focus time is saved separately from task completion. Mark a task done only after you finish the work.</p>
            {overview.recentSessions?.length > 0 && <div className="hm-card mt-6 rounded-md p-5">
              <div className="overline mb-4">Recent focus blocks</div>
              <div className="space-y-3">
                {overview.recentSessions.map((session) => <div key={session.id} className="flex items-center justify-between gap-4 border-b border-white/5 pb-3 text-sm last:border-0 last:pb-0">
                  <div className="min-w-0"><div className="truncate text-[#F5F5F0]">{session.taskText || "Open focus block"}</div><div className="text-xs text-[#A1A1AA]">{session.completedAt ? new Date(session.completedAt).toLocaleString() : ""}</div></div>
                  <div className="flex shrink-0 items-center gap-1 text-[#A1A1AA]"><Timer className="h-3.5 w-3.5" /> {session.durationMinutes} min</div>
                </div>)}
              </div>
            </div>}
          </>
        ) : <div className="hm-card rounded-md p-6 text-sm text-[#A1A1AA]">The focus room is unavailable. Reload to try again.</div>}
      </div>
    </AppShell>
  );
}
